import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "@/components/ui/sonner";
import { ESTADOS_BR } from "@/lib/estados";
import { useMunicipios } from "@/lib/municipios";
import { cn } from "@/lib/utils";
import { useSalvarLocalizacao } from "./api";
import {
  LINK_CONSULTA_CNES,
  SITUACOES_ANVISA,
  linkConsultaAnvisa,
  localizacaoSchema,
  rotuloEstabelecimento,
  type LocalizacaoFormValues,
} from "./schema";

/** O que a linha da fila (ou da lista de localizados) já sabe sobre o estabelecimento. */
export interface AlvoLocalizacao {
  /** Sem CNPJ (estabelecimento público), a localização é gravada pelo CNES. */
  cnpj?: string;
  cnes?: string;
  codigoIbge?: string;
  razaoSocial?: string;
  situacaoAnvisa?: string;
  observacao?: string;
  /** Só sugestão de UF quando ainda não há município (UF do CRF do RT). */
  ufSugerida?: string;
}

interface DialogLocalizacaoProps {
  alvo: AlvoLocalizacao | null;
  onClose: () => void;
}

export function DialogLocalizacao({ alvo, onClose }: DialogLocalizacaoProps) {
  const municipios = useMunicipios();
  const salvar = useSalvarLocalizacao();
  const form = useForm<LocalizacaoFormValues>({
    resolver: zodResolver(localizacaoSchema),
    defaultValues: { uf: "", codigoIbge: "", razaoSocial: "", situacaoAnvisa: undefined, observacao: "" },
  });

  // Reset a cada abertura com o estado atual do registro, sem inventar valor (D14).
  useEffect(() => {
    if (!alvo) return;
    const atual = alvo.codigoIbge ? municipios.data?.porCodigo.get(alvo.codigoIbge) : undefined;
    form.reset({
      uf: atual?.uf ?? alvo.ufSugerida ?? "",
      codigoIbge: alvo.codigoIbge ?? "",
      razaoSocial: alvo.razaoSocial ?? "",
      situacaoAnvisa: alvo.situacaoAnvisa,
      observacao: alvo.observacao ?? "",
    });
  }, [alvo, municipios.data, form]);

  const uf = useWatch({ control: form.control, name: "uf" });

  function onSubmit(values: LocalizacaoFormValues) {
    if (!alvo) return;
    const { codigoIbge, razaoSocial, situacaoAnvisa, observacao } = values;
    salvar.mutate(
      { chave: { cnpj: alvo.cnpj, cnes: alvo.cnes }, body: { codigoIbge, razaoSocial, situacaoAnvisa, observacao } },
      {
        onSuccess: () => {
          toast.success("Localização registrada.");
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={alvo !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Localização do {alvo && rotuloEstabelecimento(alvo)}</DialogTitle>
          {alvo?.cnpj || !alvo ? (
            <DialogDescription>
              Confira o CNPJ na{" "}
              <a
                href={alvo?.cnpj ? linkConsultaAnvisa(alvo.cnpj) : undefined}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
              >
                consulta pública da Anvisa <ExternalLink className="h-3 w-3" aria-hidden />
              </a>{" "}
              e registre o município do estabelecimento.
            </DialogDescription>
          ) : (
            <DialogDescription>
              Estabelecimento público sem CNPJ. Busque o CNES na{" "}
              <a
                href={LINK_CONSULTA_CNES}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
              >
                consulta do CNES (DATASUS) <ExternalLink className="h-3 w-3" aria-hidden />
              </a>{" "}
              e registre o município do estabelecimento.
            </DialogDescription>
          )}
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, () =>
              toast.error("Existem campos inválidos. Verifique os campos destacados em vermelho."),
            )}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
              <FormField
                control={form.control}
                name="uf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UF</FormLabel>
                    <Select
                      value={field.value}
                      // Radix dispara "" ao montar/limpar (D14): ignora em vez de apagar.
                      onValueChange={(v) => {
                        if (!v || v === field.value) return;
                        field.onChange(v);
                        form.setValue("codigoIbge", "");
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="UF" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ESTADOS_BR.map((e) => (
                          <SelectItem key={e.value} value={e.value}>
                            {e.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="codigoIbge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Município</FormLabel>
                    <SeletorMunicipio
                      uf={uf}
                      value={field.value}
                      onChange={field.onChange}
                      opcoes={uf ? (municipios.data?.porUf.get(uf) ?? []) : []}
                      carregando={municipios.isLoading}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="razaoSocial"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razão social</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="situacaoAnvisa"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Situação na Anvisa</FormLabel>
                  <Select value={field.value ?? ""} onValueChange={(v) => v && field.onChange(v)}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SITUACOES_ANVISA.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="observacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observação</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={salvar.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

interface SeletorMunicipioProps {
  uf: string;
  value: string;
  onChange: (codigoIbge: string) => void;
  opcoes: { codigoIbge: string; nome: string }[];
  carregando: boolean;
}

function SeletorMunicipio({ uf, value, onChange, opcoes, carregando }: SeletorMunicipioProps) {
  const [aberto, setAberto] = useState(false);
  const selecionado = opcoes.find((m) => m.codigoIbge === value);

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={aberto}
            disabled={!uf || carregando}
            className={cn("w-full justify-between font-normal", !selecionado && "text-muted-foreground")}
          >
            {selecionado?.nome ?? (uf ? "Selecione o município" : "Selecione a UF primeiro")}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar município..." />
          <CommandList>
            <CommandEmpty>Nenhum município encontrado.</CommandEmpty>
            <CommandGroup>
              {opcoes.map((m) => (
                <CommandItem
                  key={m.codigoIbge}
                  value={`${m.nome} ${m.codigoIbge}`}
                  onSelect={() => {
                    onChange(m.codigoIbge);
                    setAberto(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", m.codigoIbge === value ? "opacity-100" : "opacity-0")} />
                  {m.nome}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
